import React from "react";
import { motion } from "framer-motion";
import {
  Globe,
  TrendingUp,
  CheckCircle,
  Shield,
  Users,
  Leaf,
  Award,
  ArrowRight,
  Target,
  BarChart3,
  Heart,
  Lightbulb,
  Zap,
  Gauge,
  Trophy,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const GSTC_Certification_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0f4d7a] via-[#1a6fa8] to-[#2589c8] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Globe size={18} className="text-blue-300" />
            <span>Sustainable Tourism Leadership</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            GSTC Certification
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Demonstrate commitment to environmental sustainability, cultural
            preservation, and social responsibility through globally recognized
            sustainable tourism certification standards.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is GSTC Certification?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                GSTC certification is an internationally recognized
                certification verifying that tourism businesses meet global
                sustainability standards in four main areas: environmental
                sustainability, social sustainability, cultural sustainability,
                and economic sustainability.
              </p>
              <p>
                The certification is based on the GSTC Criteria, consisting of
                over 40 sustainability criteria organized into four key pillars.
                Businesses meeting these criteria demonstrate commitment to
                responsible tourism.
              </p>
              <p className="font-semibold text-lg text-blue-700">
                Four Key Sustainability Pillars:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Environmental Responsibility",
                  "Social Responsibility",
                  "Cultural Preservation",
                  "Economic Sustainability",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-blue-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of GSTC Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Leaf,
                title: "Environmental Responsibility",
                desc: "Minimizes environmental impact through energy efficiency, waste management, and water conservation.",
              },
              {
                icon: Users,
                title: "Social Responsibility",
                desc: "Promotes fair wages, safe working conditions, and local community involvement.",
              },
              {
                icon: Heart,
                title: "Cultural Sustainability",
                desc: "Preserves cultural heritage through engagement with local communities and traditions.",
              },
              {
                icon: BarChart3,
                title: "Economic Sustainability",
                desc: "Supports local economies through employment opportunities and sustainable economic growth.",
              },
              {
                icon: Target,
                title: "Transparency and Accountability",
                desc: "Reports on sustainability performance ensuring transparency and encourages continuous improvement.",
              },
              {
                icon: Award,
                title: "Global Compliance Framework",
                desc: "Aligns with internationally recognized sustainability standards and best practices.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-blue-500 transition-all"
                >
                  <Icon size={32} className="text-blue-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why GSTC Certification Matters for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Demonstrate Sustainability Leadership",
                  desc: "Shows commitment to sustainable tourism by adhering to internationally recognized standards.",
                },
                {
                  title: "Enhance Market Competitiveness",
                  desc: "Attracts eco-conscious travelers seeking sustainable practices and responsible tourism.",
                },
                {
                  title: "Regulatory Compliance",
                  desc: "Ensures compliance with local, national, and international sustainability regulations.",
                },
                {
                  title: "Support Responsible Travel",
                  desc: "Provides clear signal that business is aligned with responsible tourism and sustainable travel.",
                },
                {
                  title: "Global Recognition and Access",
                  desc: "Recognized worldwide by travel companies and tourism boards, increasing market access.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500"
                >
                  <Star
                    size={24}
                    className="text-blue-600 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of GSTC Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Trophy,
                title: "Sustainability Leadership Position",
                desc: "Attracts growing base of eco-conscious travelers and responsible tourism operators.",
              },
              {
                icon: TrendingUp,
                title: "Increased Customer Demand",
                desc: "Consumers choosing travel destinations prioritizing sustainable options.",
              },
              {
                icon: Zap,
                title: "Operational Cost Savings",
                desc: "Reduced energy, waste, and water usage leading to significant cost savings.",
              },
              {
                icon: Globe,
                title: "Global Recognition and Access",
                desc: "Recognized worldwide, gaining access to international markets and tourism networks.",
              },
              {
                icon: Shield,
                title: "Regulatory Compliance",
                desc: "Ensures compliance with international sustainability regulations and local requirements.",
              },
              {
                icon: Award,
                title: "Enhanced Brand Reputation",
                desc: "Demonstrates commitment to sustainability and attracts responsible stakeholders.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-blue-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Achieve GSTC Certification
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Complete the Self-Assessment",
                  desc: "Assess sustainability practices using GSTC Criteria and identify areas of improvement.",
                },
                {
                  step: "2",
                  title: "Develop an Action Plan",
                  desc: "Create action plan to address sustainability gaps focusing on environmental, social, and cultural improvements.",
                },
                {
                  step: "3",
                  title: "Documentation and Evidence",
                  desc: "Prepare documentation demonstrating compliance with GSTC Criteria including policies and reports.",
                },
                {
                  step: "4",
                  title: "Third-Party Audit",
                  desc: "Undergo audit by GSTC-accredited auditor to verify compliance with GSTC Criteria.",
                },
                {
                  step: "5",
                  title: "Certification and Monitoring",
                  desc: "Receive GSTC certification after successful audit with ongoing monitoring requirements.",
                },
                {
                  step: "6",
                  title: "Continuous Improvement",
                  desc: "Maintain certification through regular reviews and continuous sustainability enhancements.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-blue-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from GSTC Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Hotel and Accommodation Services",
              "Tour and Travel Operators",
              "Hospitality and Resort Management",
              "Ecotourism and Adventure Tourism",
              "Destination Marketing Organizations",
              "Transportation and Tourism Services",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-blue-500 text-center"
              >
                <Globe size={32} className="text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#0f4d7a] via-[#1a6fa8] to-[#2589c8] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <Globe size={40} className="mx-auto mb-4 text-blue-300" />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for GSTC Certification?
            </h3>
            <p className="text-blue-100 mb-6 text-sm leading-relaxed">
              MBA Solutions provides expert GSTC certification consultancy,
              guiding tourism businesses through the certification process and
              ensuring compliance with global sustainability standards.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-blue-300 flex-shrink-0"
                />
                GSTC Self-Assessment and Gap Analysis
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-blue-300 flex-shrink-0"
                />
                Sustainability Strategy Development
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-blue-300 flex-shrink-0"
                />
                Sustainable Tourism Practices Implementation
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-blue-300 flex-shrink-0"
                />
                Third-Party Audit Preparation
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-blue-300 flex-shrink-0"
                />
                Ongoing Monitoring and Re-certification Support
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-blue-200"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-200"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-blue-700 font-bold hover:bg-cyan-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default GSTC_Certification_Details;
